import { useState } from 'react';
import { Button } from 'equasion-lib';
import { FadeLoader } from 'react-spinners';
import { FiAlertTriangle, FiMinus, FiPlus } from 'react-icons/fi';
import ZoneSelectWithScan from '@/lib/shared-coms/ZoneSelectWithScan';
import { eqProps } from '../eqProps';
import {
  QUARANTINE_FIT_TO_SHIP_NOTE,
  QUARANTINE_NO_PERMISSION_MESSAGE,
  quarantineExplanation,
  quarantineLotLabel,
  quarantineOptionKey,
  quarantineUnitLabel,
  stockQuantityProblem,
  suggestedZoneNote,
  type VerifyQuarantineView,
  type VerifyQuarantineZone,
  type VerifyStockChoice
} from '../verifyQuarantine';

type VerifyQuarantineSheetProps = {
  companyId: string;
  /** Null while the prompt's details load. */
  view: VerifyQuarantineView | null;
  /** Scans still saving: "Stock & verify" waits for them (one Verify write at a time). */
  pending: number;
  /** The stock is being sent. */
  busy: boolean;
  /** The server's refusal of the last "Stock & verify", shown in place. */
  error: string | null;
  onStock: (choice: VerifyStockChoice) => void;
  onCancel: () => void;
};

const QUARANTINE_SUFFIX = / \(Quarantine\)$/;

/**
 * The Verify screen's quarantine prompt (verifyQuarantine.ts): a bottom sheet over the order, which
 * the operator never leaves. It names the product and where the unit is recorded, and offers
 * "Stock & verify" — which lot (bulk, when several are quarantined), how many to stock (bulk), and
 * the stock zone, offered pre-filled (the zone itself for a unit quarantined in place, else the zone
 * Pick took the product from) and changed by scanning a zone label in the sheet or choosing from the
 * list (stock zones at the unit's own location only). Cancel changes nothing. A user who may not
 * stock inventory is told so, with only a way back.
 */
function VerifyQuarantineSheet({
  companyId,
  view,
  pending,
  busy,
  error,
  onStock,
  onCancel
}: VerifyQuarantineSheetProps) {
  const options = view?.options || [];
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const selectedIndex = Math.max(
    0,
    options.findIndex((option, index) => quarantineOptionKey(option, index) === selectedKey)
  );
  const option = options[selectedIndex] ?? null;
  // The zone the operator chose; until then, the option's own suggestion.
  const [chosenZone, setChosenZone] = useState<VerifyQuarantineZone | null>(null);
  const zone =
    chosenZone ??
    (option?.suggestedStockZone?.id ? option.suggestedStockZone : null);
  const [quantityText, setQuantityText] = useState<string | null>(null);

  if (!view) {
    return (
      <div className="verify-quarantine" aria-busy="true">
        <p className="verify-quarantine__badge">
          <FiAlertTriangle aria-hidden /> In quarantine
        </p>
        <div className="verify-quarantine__loading">
          <FadeLoader color="var(--n0)" />
          <span>Checking where it is recorded…</span>
        </div>
        <Button type="secondary" fullWidth {...eqProps({ onClick: onCancel })}>
          Cancel
        </Button>
      </div>
    );
  }

  const serial = view.reason === 'serial';
  const least = Math.max(1, view.quantity || 1);
  const most = Math.max(least, option?.quarantined || least);
  const quantityValue = quantityText ?? String(least);
  const quantityIssue = serial ? null : stockQuantityProblem(quantityValue, least, most);
  const quantity = serial || quantityIssue ? null : Number(quantityValue.trim());
  const unitLabel = quarantineUnitLabel(view, option);
  const zoneNote = !chosenZone && zone ? suggestedZoneNote(option) : null;
  const ready =
    view.canStock && !!option && !!zone?.id && (serial || quantity !== null) && !busy;

  const chooseOption = (key: string) => {
    if (key === quarantineOptionKey(option!, selectedIndex)) return;
    const next = options.find((candidate, index) => quarantineOptionKey(candidate, index) === key);
    setSelectedKey(key);
    // A zone chosen for another location cannot take this lot; one at the same location still can.
    if (chosenZone && next?.locationId !== option?.locationId) setChosenZone(null);
    setQuantityText(null);
  };

  const submit = () => {
    if (!ready || !option || !zone) return;
    onStock({
      stockZoneId: zone.id,
      balanceId: option.balanceId ?? null,
      quantity: serial ? null : quantity
    });
  };

  const step = (delta: number) => {
    const current = Number(quantityValue.trim());
    const base = Number.isFinite(current) ? current : least;
    setQuantityText(String(Math.min(most, Math.max(least, Math.round(base) + delta))));
  };

  return (
    <div className="verify-quarantine">
      <p className="verify-quarantine__badge">
        <FiAlertTriangle aria-hidden /> In quarantine
      </p>
      <p className="verify-sheet__title verify-quarantine__product">{view.productName}</p>
      {!!unitLabel && <p className="verify-quarantine__unit">{unitLabel}</p>}
      <p className="verify-sheet__text verify-quarantine__text">
        {quarantineExplanation(view, option)}
      </p>

      {!view.canStock ? (
        <>
          <p className="verify-quarantine__note" role="note">
            {QUARANTINE_NO_PERMISSION_MESSAGE}
          </p>
          <div className="verify-sheet__actions">
            <Button type="secondary" fullWidth {...eqProps({ onClick: onCancel })}>
              Back to Verify
            </Button>
          </div>
        </>
      ) : (
        <>
          {options.length > 1 && (
            <div className="verify-quarantine__section">
              <span className="verify-quarantine__label">Which lot are you holding?</span>
              <div className="verify-quarantine__options" role="radiogroup" aria-label="Quarantined lot">
                {options.map((candidate, index) => {
                  const key = quarantineOptionKey(candidate, index);
                  const checked = index === selectedIndex;
                  return (
                    <button
                      key={key}
                      type="button"
                      role="radio"
                      aria-checked={checked}
                      disabled={busy}
                      className={['verify-quarantine__option', checked && 'active']
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => chooseOption(key)}
                    >
                      <span className="verify-quarantine__option-lot">
                        {quarantineLotLabel(candidate)}
                      </span>
                      <span className="verify-quarantine__option-meta">
                        {candidate.zone?.name || 'Quarantine'}
                      </span>
                      <span className="verify-quarantine__option-units">
                        {candidate.quarantined} in quarantine
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {!serial && (
            <div className="verify-quarantine__section">
              <span className="verify-quarantine__label" id="verify-quarantine-quantity">
                Units to stock
              </span>
              <div className="verify-quarantine__stepper">
                <button
                  type="button"
                  aria-label="One fewer"
                  disabled={busy || (quantity ?? least) <= least}
                  onClick={() => step(-1)}
                >
                  <FiMinus aria-hidden />
                </button>
                <input
                  name="stockQuantity"
                  inputMode="numeric"
                  autoComplete="off"
                  aria-labelledby="verify-quarantine-quantity"
                  value={quantityValue}
                  disabled={busy}
                  onChange={(e) => setQuantityText(e.target.value)}
                />
                <button
                  type="button"
                  aria-label="One more"
                  disabled={busy || (quantity ?? least) >= most}
                  onClick={() => step(1)}
                >
                  <FiPlus aria-hidden />
                </button>
              </div>
              <small className="verify-quarantine__hint">
                {quantityIssue ||
                  (quantity !== null && quantity > least
                    ? `${least} verified now; the other ${quantity - least} go into stock.`
                    : `${most} in quarantine there.`)}
              </small>
            </div>
          )}

          <div className="verify-quarantine__section verify-quarantine__zone">
            <ZoneSelectWithScan
              // A new location is a new zone search: the field starts over.
              key={option?.locationId || 'any'}
              form={false}
              label="Stock zone"
              scanTitle="Scan the stock zone's label"
              companyId={companyId}
              locationId={option?.locationId || undefined}
              zoneTypes={['stock']}
              value={zone?.id || ''}
              valueDisplay={zone?.name || undefined}
              onChange={(selection) =>
                setChosenZone(
                  selection?.value
                    ? {
                        id: selection.value,
                        name: selection.display.replace(QUARANTINE_SUFFIX, ''),
                        zoneType: selection.zoneType || 'stock'
                      }
                    : null
                )
              }
            />
            {!!zoneNote && <small className="verify-quarantine__hint">{zoneNote}</small>}
          </div>

          <p className="verify-quarantine__note" role="note">
            {QUARANTINE_FIT_TO_SHIP_NOTE}
          </p>
          {!!error && (
            <p className="verify-quarantine__error" role="alert">
              {error}
            </p>
          )}

          <div className="verify-sheet__actions">
            <Button
              type="primary"
              fullWidth
              disabled={!ready || pending > 0}
              {...eqProps({ onClick: submit })}
            >
              {busy
                ? 'Stocking…'
                : pending > 0
                ? `Saving ${pending} scan(s)…`
                : 'Stock & verify'}
            </Button>
            <Button
              type="secondary"
              fullWidth
              disabled={busy}
              {...eqProps({ onClick: onCancel })}
            >
              Cancel — leave in quarantine
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default VerifyQuarantineSheet;
